/* global globalThis */
import React, { Suspense, useEffect, useRef, useState, useCallback } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useDispatch, connect } from 'react-redux';
import { Badge, Spin } from 'antd';
import PropTypes from 'prop-types';

import Layout from './components/Layout';
import { menuItems } from './components/Navigation/Menu';
import { Ajax } from './components/Ajax';
import { ApiUrl, AppPages, StoreKeys } from './project/Defines';
import { Project } from './project/Project';
import { storageChange, /*changeApplicationToken,*/ changeUserToken } from './store/actions';
import Loader from './Shared/Loader';

const Login = React.lazy(() => import('./pages/Login'));
const LoginRedirect = React.lazy(() => import('./pages/LoginRedirect'));
const LoginCallback = React.lazy(() => import('./pages/LoginCallback'));
const Logout = React.lazy(() => import('./pages/Logout'));
const LogoutRedirect = React.lazy(() => import('./pages/LogoutRedirect'));
const ConnectAs = React.lazy(() => import('./pages/ConnectAs/ConnectAs'));
const NotFound = React.lazy(() => import('./pages/NotFound'));
const Template = React.lazy(() => import('./pages/Template').then(module => ({ default: module.Template })));
const Template2 = React.lazy(() => import('./pages/Template').then(module => ({ default: module.Template2 })));
const Planning = React.lazy(() => import('./pages/Planning'));
const ServiceCalls = React.lazy(() => import('./pages/ServiceCall/index'));
const UnassignedServiceCalls = React.lazy(() => import('./pages/ServiceCall/UnassignedServiceCalls'));
const ServiceCallsDetails = React.lazy(() => import('./pages/ServiceCall/detail'));
const ServiceCallCreate = React.lazy(() => import('./pages/ServiceCall/Create/CreateCall'));
const Fees = React.lazy(() => import('./pages/Documents/Fee'));
const SpareParts = React.lazy(() => import('./pages/Documents/SparePart'));
const DPC = React.lazy(() => import('./pages/Documents/DPC'));
const InvoiceGeneration = React.lazy(() => import('./pages/Invoice/index'));
const ServiceContract = React.lazy(() => import('./pages/ServiceContract'));
const ServiceContractDetails = React.lazy(() => import('./pages/ServiceContract/detail'));
const ServiceContractCreate = React.lazy(() => import('./pages/ServiceContract/Create/CreateContract'));
const ResourceRedirect = React.lazy(() => import('./components/ResourceRedirect'));

const routings = [
  { page: AppPages.Login, component: Login },
  { page: AppPages.Logout, component: Logout },
  { page: AppPages.Template, component: Template },
  // { page: AppPages.RedirectResource, component: ResourceRedirect },
  { page: AppPages.Planning, component: Planning },
  { page: AppPages.ServiceCallsDetails, component: ServiceCallCreate, routeParams: "create" },
  { page: AppPages.ServiceCallsDetails, component: ServiceCallsDetails, routeParams: ':id' },
  { page: AppPages.ServiceCalls, component: ServiceCalls },
  { page: AppPages.ServiceCallsUnassigned, component: UnassignedServiceCalls },
  // { page: AppPages.Documents, component: Documents },
  { page: AppPages.Fees, component: Fees },
  { page: AppPages.SpareParts, component: SpareParts },
  { page: AppPages.DPC, component: DPC },
  { page: AppPages.InvoiceGeneration, component: InvoiceGeneration },
  // { page: AppPages.Attachments, component: Attachments },
  // { page: AppPages.AttachmentDetails, component: AttachmentDetails, routeParams: ':id' },
  { page: AppPages.ServiceContractDetails, component: ServiceContractCreate, routeParams: "create" },
  { page: AppPages.ServiceContractDetails, component: ServiceContractDetails, routeParams: ':id' },
  { page: AppPages.ServiceContract, component: ServiceContract }
];

AppDefault.propTypes = {
  role: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  storageChange: PropTypes.func,
  isMaintenanceModeEnabled: PropTypes.bool,
  maintenanceModeStart: PropTypes.any,
  maintenanceModeEnd: PropTypes.any,
  enableServiceContract: PropTypes.bool,
};

/**
*  the Base component that also manages routing
 * @module AppDefault
 */
function AppDefault(props) {
  const { role, isLoggedIn, storageChange, isMaintenanceModeEnabled, maintenanceModeStart, maintenanceModeEnd, enableServiceContract } = props;
  const loading = <div style={{ "margin": "25% auto" }}><Spin size="large" /></div>;
  const homeRedirect = Project.getPageUrl(menuItems.find(i => Project.hasRoleAccess(role, i.to))?.to);
  const [showPages, setShowPages] = useState(false);
  const [isMaintainance, setIsMaintainance] = useState(false);
  const appRef = useRef(false);
  const dispatch = useDispatch();
  const [maintainanceData, setMaintainanceData] = useState(null);

  useEffect(() => {
    appRef.current = true;
    const handler = e => {
      const trackingKeys = [StoreKeys.UserToken, StoreKeys.UserName, StoreKeys.LoggedIn, StoreKeys.ListStates, StoreKeys.Culture, StoreKeys.Role, StoreKeys.ExpireTime];
      if (trackingKeys.includes(e.key)) {
        storageChange();
      }
    };

    setTimeout(() => {
      if (isLoggedIn) {
        Ajax.post({
          url: ApiUrl.AccountCheck,
          data: {},
          success: function (response) {
            if (response) {
              setShowPages(true);
            }
          }
        });
      } else {
        setShowPages(true);
      }
    }, 100);

    setInterval(()=>{
      if (!Project.appToken) {
        refreshAppToken();
      }
       
      refreshUserToken();
    }, 500);

    Project.initGoogleKey();
    
    if (isMaintenanceModeEnabled) {
      let now = new Date();
      if (maintenanceModeEnd >= now) {

        if (maintenanceModeStart <= now) {
          setIsMaintainance(true);
          setTimeout(() => { appRef.current && setIsMaintainance(false); }, maintenanceModeEnd - new Date());
        } else {
          setTimeout(() => {
            appRef.current && setIsMaintainance(true);
            setTimeout(() => { appRef.current && setIsMaintainance(false); }, maintenanceModeEnd - new Date());
          }, maintenanceModeStart - now);
        }
      }
    }

    Project.getMaintenance((maintenance)=>{ 
        if (maintenance) {
            setMaintainanceData(maintenance)
        }
    });

    globalThis.addEventListener('storage', handler);
    return () => {
      appRef.current = false;
      globalThis.removeEventListener('storage', handler);
    };
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const refreshAppToken = useCallback(() => {
    const _appToken = Project._getLocalStorage("appToken");
    const isLoadingAppToken = Project._getLocalStorage("isLoadingAppToken");
    if (!_appToken && !isLoadingAppToken){
        Project._setLocalStorage("isLoadingAppToken", true, 10);

        let fd =  "grant_type=client_credentials";
        let headers = { "Content-Type": "application/x-www-form-urlencoded" };
        const fetchOptions = { 
            method: "POST",
            body: fd
        };
        let params = {
            ...fetchOptions, 
            headers: headers
        };

        fetch(`${process.env.REACT_APP_API_TOKEN_URL ?? globalThis.location.origin}/api/token`, params)
        .then((response) => {
            return response.json()
        })
        .then((response) => {
            if (response?.access_token){
                Project._setLocalStorage("appToken", response, response.expires_in - 10);
            }
            Project._setLocalStorage("isLoadingAppToken", false, 30);
        })
        .catch((error) =>{
            console.log(error);
            Project._setLocalStorage("isLoadingAppToken", false);
        });

    }
  },[dispatch]);// eslint-disable-line react-hooks/exhaustive-deps

  const refreshUserToken = useCallback(() => {
    const isLoadingUserToken = Project.getLocalStorage("isLoadingUserToken");
    if (!isLoadingUserToken && isLoggedIn){
        Project.getLocalStorage("userToken", (expireData) => {
            Project.setLocalStorage("isLoadingUserToken", true, 30);
            const callback = `${globalThis.location.origin}/wso/callback`;

            let fd = "";
            fd += "grant_type=refresh_token";
            fd += "&redirect_uri=" + callback;
            fd += "&refresh_token=" + expireData.refresh_token;
            let headers = { "Content-Type": "application/x-www-form-urlencoded" };
            const fetchOptions = { 
                method: "POST",
                body: fd
            };
            let params = {
                ...fetchOptions, 
                headers: headers
            };

            fetch(`${process.env.REACT_APP_API_TOKEN_URL ?? globalThis.location.origin}/api/token`, params)
            .then((response) => {
                return response.json()
            })
            .then((response) => {
                if (response?.access_token){
                    Project.setLocalStorage("userToken", response, response.expires_in - 600);
                    dispatch(changeUserToken(response.access_token));
                    Project.setLocalStorage("isLoadingUserToken", false, 30);
                }
                Project.setLocalStorage("isLoadingUserToken", false, 30);
            })
            .catch((error) =>{
                console.log(error);
            });
            
        });
        
    }
  },[isLoggedIn, dispatch]);// eslint-disable-line react-hooks/exhaustive-deps


  if (maintainanceData?.enabled) {

        const langs = [
          {lang: 'fr'}, 
          {lang: 'es'}, 
          {lang: 'it'},
          {lang: 'en'}, 
        ];
        const maintainanceTexts = [];
        for(const lang of langs) {
         let messages = (maintainanceData[lang.lang] || maintainanceData['fr']).split(String.raw`\r`);
         let texts = [];

         for(const message of messages){
            texts.push(<p>{message}</p>);
         }

         maintainanceTexts.push(
            <div>
                <h1>{lang.lang}</h1>
                {texts}
            </div>);
        }


        return <view>
        <maintainance>
          <cont>
            { maintainanceData?.enabled && maintainanceTexts }
        </cont>
        </maintainance>
        </view>;
    }


  if (isMaintainance) {
    return <h1> Maintenance <Badge>{maintenanceModeStart.toLocaleString()}-{maintenanceModeEnd.toLocaleString()}</Badge> </h1>;
  }
  if (!isLoggedIn) {
    
    return <Suspense fallback={loading}>
      <Layout>
        <Switch>
          {/* <Route path={.RedirectResource} component={ResourceRedirect}/> */}
          <Route
            path="/loginredirect/:code"
            component={LoginRedirect}
          />
          <Route
            path="/connectas/:token"
            component={ConnectAs}
          />
          <Route
            path="/login"
            component={Login}
          />
          <Route
            path="/wso/callback"
            component={LoginCallback}
          />
          <Route path='/template' component={Template} />
          <Route path='/templatedetails' component={Template2} />
          <Route path="*" >
            <Redirect to={Project.getPageUrl(AppPages.Login, "", { redirectUrl: globalThis.location.pathname })} />
          </Route>
        </Switch>
      </Layout>
    </Suspense>;
  }

  if (!showPages)
    return <>{loading}</>;

  return <Suspense fallback={loading}>
    <Layout>
      <Switch>
        <Route
          path="/go" 
          component={ResourceRedirect}
        />
        <Route exact path='/' >
          {(homeRedirect && <Redirect to={homeRedirect} />) || (<Redirect to={Project.getPageUrl(AppPages.Login)} />)}
        </Route>
        <Route
          path="/logoutredirect"
          component={LogoutRedirect}
        />
        <Route path="/login">
          {(homeRedirect && <Redirect to={homeRedirect} />) || (<Redirect to={Project.getPageUrl(AppPages.Login)} />)}
        </Route>
        <Route
          path="/wso/callback"
          component={LoginCallback}
        />
        {routings.filter(r => (r.pages ? r.pages.some(p => Project.hasRoleAccess(role, p)) : Project.hasRoleAccess(role, r.page))
          && (enableServiceContract || ![AppPages.ServiceContract, AppPages.ServiceContractDetails].includes(r.page)))
          .map(r =>
            <Route key={r.pages ? r.pages.join() : r.page} path={r.pages ? r.pages.map(p => Project.getPageUrl(p.page || p, p.routeParams)) : Project.getPageUrl(r.page, r.routeParams)} component={r.component} />)}

        <Route path="*" component={NotFound} />
      </Switch>
    </Layout>
  </Suspense>;
}

const AppDefaultComponent = connect(state => ({
  isLoggedIn: state.isLoggedIn,
  role: state.role,
  isMaintenanceModeEnabled: state.isMaintenanceModeEnabled,
  maintenanceModeStart: state.maintenanceModeStart,
  maintenanceModeEnd: state.maintenanceModeEnd,
  enableServiceContract: state.enableServiceContract
}),
  dispatch => ({
    storageChange: (key) => dispatch(storageChange())
  }))(AppDefault);


const AppRedirectDefaultComponent = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
  }, []);

  return (
    <>
      { isLoading ? <Loader force={isLoading}/> : <AppDefaultComponent />}
    </>
  )
}

const App = () => {
  return <>
    {
      globalThis.location.pathname === '/go' 
      ? <AppRedirectDefaultComponent/> : <AppDefaultComponent />}
  </>
}
export default App;
